import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import {
    DropdownItem,
    Tag,
    Text,
    Link,
    HelpTip,
    Flex,
    Divider,
} from "@amzn/storm-ui";
import FilterDropdown from "@amzn/storm-ui-filter-dropdown";
import React, { useEffect, useState } from "react";

import { SALESFORCE_ACCOUNT_ACCESS_URL } from "../../constants/links";
import {
    TELEMETRY_ID,
    PHOENIX_FEATURE,
    PHOENIX_TELEMETRY_ID,
} from "../../constants/telemetry";
import { useSfAccountsAction } from "../../hooks/salesforce/useSfAccountsAction";
import { logWithTakt } from "../../utils/telemetry.utils";

const NO_ACCOUNT_OPTION = { value: "", label: "No Account" };

export const DEFAULT_ACCOUNT_LABEL = "Choose Salesforce account";
export const SPOOFED_ACCOUNT_LABEL = "Spoofed Account";
export interface AccountDropdownProps {
    selectedAccountId: string | null;
    onAccountSelect: (accountId: string) => void;
    onCancel?: () => void;
    showNoAccount?: boolean;
    overrideDisplayLabel?: string;
}

export const AccountDropdown: React.FunctionComponent<AccountDropdownProps> = ({
    selectedAccountId,
    onAccountSelect,
    onCancel,
    showNoAccount = false,
    overrideDisplayLabel,
}) => {
    const [accountsState, accountsAction] = useSfAccountsAction();
    const [filteredAccounts, setFilteredAccounts] = useState<
        Array<{ value: string; label: string; tag?: string; test?: boolean }>
    >([]);
    const [loading, setLoading] = useState<boolean>(!selectedAccountId);
    const hasNoAccounts = !loading && accountsState.result?.length === 0;

    useEffect(() => {
        void accountsAction.execute();
    }, [accountsAction]);

    useEffect(() => {
        if (accountsState.result) {
            const accounts = showNoAccount
                ? [...accountsState.result, NO_ACCOUNT_OPTION]
                : accountsState.result;
            setFilteredAccounts(accounts);
            setLoading(false); // Only set loading to false when we have results
        }
    }, [accountsState.result, showNoAccount]);

    const isAccountValid = selectedAccountId
        ? (accountsState.result ?? []).some(
              (account) => account.value === selectedAccountId,
          )
        : true;

    const handleFilterChange = (results) => {
        const isSearchActive =
            results.length < (accountsState.result?.length ?? 0);

        const regularAccounts = results.filter(
            (item) => item.value !== NO_ACCOUNT_OPTION.value,
        );
        const finalResults = isSearchActive
            ? results // During search, show only matching results
            : showNoAccount // When no search, append NO_ACCOUNT if needed
              ? [...results, NO_ACCOUNT_OPTION]
              : results;

        setFilteredAccounts(finalResults);
        logWithTakt(TELEMETRY_ID.ACCOUNT_DROPDOWN_SEARCH, {
            searchResultCount: regularAccounts.length,
        });
        PhoenixMetrics.emitCount(
            PHOENIX_TELEMETRY_ID.ACCOUNT_DROPDOWN_SEARCH_COUNT,
            regularAccounts.length,
            PHOENIX_FEATURE.ACCOUNT_PICKER,
        );
    };

    const handleFilterClear = () => {
        setFilteredAccounts(
            showNoAccount
                ? [...(accountsState.result ?? []), NO_ACCOUNT_OPTION]
                : (accountsState.result ?? []),
        );
    };

    const handleDropdownChange = (value: string) => {
        onAccountSelect(value);
        logWithTakt(TELEMETRY_ID.ACCOUNT_DROPDOWN_SELECTION, {
            selectedAccountId: value,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.ACCOUNT_DROPDOWN_SELECTION,
            PHOENIX_FEATURE.ACCOUNT_PICKER,
            {
                customData: {
                    selectedAccountId: value,
                },
            },
        );
    };

    const overrideLabel = (label: string, value: string) => {
        if (loading && !selectedAccountId) {
            return "Account loading..."; // Show loading state first
        }

        if (overrideDisplayLabel && !isAccountValid) {
            return overrideDisplayLabel;
        }
        if (hasNoAccounts) {
            return "No accounts available";
        }
        if (!value) {
            return DEFAULT_ACCOUNT_LABEL;
        }
        const selectedAccount = (accountsState.result ?? []).find(
            (account) => account.value === value,
        );
        return selectedAccount ? selectedAccount.label : label;
    };

    const options = {
        shouldSort: true, // Sorts results by best match first
        keys: [
            {
                name: "label",
                weight: 0.7, // Label matches are 70% important
            },
            {
                name: "value",
                weight: 0.3, // Value matches are 30% important
            },
        ],
        threshold: 0.4,
        ignoreLocation: true, // Matches anywhere in string, not just beginning
        findAllMatches: true, // Finds all matching instances, not just first
        minMatchCharLength: 2, // Minimum characters needed to match

        filter: (item) => item.value !== NO_ACCOUNT_OPTION.value,
    };

    const handleManageLinkClick = () => {
        logWithTakt(TELEMETRY_ID.ACCOUNT_DROPDOWN_MANAGE_LINK_CLICK, {
            origin: window.location.origin,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.ACCOUNT_DROPDOWN_MANAGE_LINK_CLICK,
            PHOENIX_FEATURE.ACCOUNT_PICKER,
            {
                customData: {
                    origin: window.location.origin,
                },
            },
        );
    };

    const handleIHubLinkClick = () => {
        logWithTakt(TELEMETRY_ID.ACCOUNT_DROPDOWN_IHUB_LINK_CLICK, {
            origin: window.location.origin,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.ACCOUNT_DROPDOWN_IHUB_LINK_CLICK,
            PHOENIX_FEATURE.ACCOUNT_PICKER,
            {
                customData: {
                    origin: window.location.origin,
                },
            },
        );
    };

    const renderAddAdvertiserOption = () => (
        <>
            <Divider marginBlockStart="none" marginBlockEnd="none" />
            <Flex
                key="manage-advertisers"
                data-testid="manage-advertisers-link"
                padding="xxxsmall small"
            >
                <Text>
                    Missing Salesforce account? Get temporary access&nbsp;
                    <Link
                        href={`${window.location.origin}/book-of-business/manage`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleManageLinkClick}
                    >
                        here
                    </Link>
                </Text>
                <HelpTip>
                    Learn more about access to Salesforce accounts on IHub{" "}
                    <Link
                        href={SALESFORCE_ACCOUNT_ACCESS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleIHubLinkClick}
                    >
                        here
                    </Link>
                </HelpTip>
            </Flex>
        </>
    );

    return (
        <FilterDropdown
            searchInputId="account-dropdown-search-input"
            placeholder="Search accounts"
            onChange={handleDropdownChange}
            onFilterChange={handleFilterChange}
            onFilterClear={handleFilterClear}
            selectedValue={isAccountValid ? (selectedAccountId ?? "") : ""}
            onOverrideLabel={overrideLabel}
            loading={loading}
            options={options}
            items={accountsState.result ?? []}
            mobileFullScreen={false}
            secondaryViewCloseButtonLabel="Done"
            data-testid="sf-picker"
            persistFilterState={true}
            fullWidth={true}
            postRender={renderAddAdvertiserOption}
        >
            {hasNoAccounts ? (
                <DropdownItem
                    key="no-accounts"
                    value=""
                    data-testid="no-accounts-message"
                    disabled
                >
                    <Text color="secondary">No accounts available</Text>
                </DropdownItem>
            ) : (
                filteredAccounts.map((account) => (
                    <DropdownItem
                        key={account.value ?? "no-selection"}
                        value={account.value}
                        data-testid={account.value ?? "no-selection"}
                    >
                        {account.tag && (
                            <>
                                <Tag
                                    variant="violet"
                                    label={account.tag}
                                    description={account.tag}
                                />{" "}
                            </>
                        )}
                        {account.test && (
                            <Tag
                                variant="info"
                                label="Test"
                                description="Test"
                            />
                        )}
                        <Text>{account.label}</Text>
                    </DropdownItem>
                ))
            )}
        </FilterDropdown>
    );
};

export default AccountDropdown;
